import React, { useState, useEffect, useRef } from 'react';
import Geocode from 'react-geocode';
import PropTypes from 'prop-types';
import {
  GoogleMap, Marker, Polyline, Circle,
} from '@react-google-maps/api';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
  Grid,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

import LocationMarkers from '../map/location-markers';
import WranglerMarkers from '../map/wrangler-markers';
import ZonePolygons from '../map/zone-polygons';
import VehicleMarkers from '../map/vehicle-markers';
import { PickupTaskModal } from './pickup-task-modal';
import CustomMapControl from './custom-map-control';

// Google removed the Drawing Library's DrawingManager from the Maps JavaScript
// API (as of v3.65), so boundary drawing is implemented manually below: clicking
// on the map while `isDrawing` is true appends a vertex, and clicking near the
// starting vertex closes/completes the polygon.
const CLOSE_POLYGON_DISTANCE_METERS = 25;

const mapStyles = [
  {
    featureType: 'poi',
    elementType: 'labels.icon',
    stylers: [
      {
        color: '#A3B3BC',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#606F80',
      },
    ],
  },
  {
    featureType: 'landscape.man_made',
    stylers: [
      {
        color: '#F2F3F4',
      },
    ],
  },
  {
    featureType: 'water',
    stylers: [
      {
        color: '#BBCEFB',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#C9EFC9',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#606F80',
      },
    ],
  },
  {
    featureType: 'transit',
    elementType: 'labels.icon',
    stylers: [
      {
        color: '#A3B3BC',
      },
    ],
  },
  {
    featureType: 'transit',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#606F80',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'simplified',
        color: '#A3B3BC',
      },
    ],
  },
];

const MarkerTypes = {
  VEHICLE: 0,
  LOCATION: 1,
  WRANGLER: 2,
};
const { google } = window;

function BasicMap(props) {
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [selectedMarkerType, setSelectedMarkerType] = useState(null);
  const [mapZoom, setMapZoom] = useState(props.zoom);
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [polygonPath, setPolygonPath] = useState([]);
  const [hasActiveDrawingMode, setHasActiveDrawingMode] = useState(false);
  const [filteredVehicles, setFilteredVehicles] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [newPolygon, setNewPolygon] = useState(null);

  const searchRef = useRef(null);
  const [foundCoords, setFoundCoords] = useState(null);
  const mapRef = useRef(null);
  const polygonPathRef = useRef([]);

  const setSelectedMarker = (id, type) => {
    setSelectedMarkerId(id);
    setSelectedMarkerType(type);
  };
  const deselectMarker = () => {
    setSelectedMarkerId(null);
    setSelectedMarkerType(null);
  };

  const getSelectedMarketCoords = () => {
    const selectedMarket = get(props, 'selectedMarket', {});
    return {
      lat: get(selectedMarket, 'geo.coordinates[1]', 0),
      lng: get(selectedMarket, 'geo.coordinates[0]', 0),
    };
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const onDrawComplete = (polygon) => {
    if (!polygon) return;
    setNewPolygon(polygon);
    setHasActiveDrawingMode(true);
    const vehiclesContained = props.vehicles.filter((v) => {
      if (v.geo && v.geo.geoJson) {
        const coords = {
          lat: parseFloat(v.geo.geoJson.coordinates[1]),
          lng: parseFloat(v.geo.geoJson.coordinates[0]),
        };
        if (
          google.maps.geometry
          && google.maps.geometry.poly.containsLocation(coords, polygon)
        ) {
          return true;
        }
      }
      return false;
    });
    if (!vehiclesContained || isEmpty(vehiclesContained)) {
      setIsOpen(false);
      return false;
    }
    setFilteredVehicles(vehiclesContained);

    const { setSelectedVehicleIds, updatePolygonVehicles } = props;
    const selectedVehiclesIds = vehiclesContained.map((vehicle) => vehicle._id);
    setSelectedVehicleIds(selectedVehiclesIds);
    updatePolygonVehicles(vehiclesContained);
    setIsOpen(true);
  };

  const resetDrawingState = () => {
    setIsDrawing(false);
    polygonPathRef.current = [];
    setPolygonPath([]);
  };

  // The Drawing Library's DrawingManager is gone, so we build a real
  // google.maps.Polygon ourselves once the user finishes clicking out the
  // boundary, mirroring what onPolygonComplete used to hand us.
  const finishDrawing = (path) => {
    resetDrawingState();
    if (path.length < 3) return;
    const polygon = new google.maps.Polygon({ paths: path, map: mapRef.current });
    onDrawComplete(polygon);
  };

  const onMapClick = (event) => {
    if (!isDrawing) return;
    const currentPath = polygonPathRef.current;
    if (currentPath.length >= 3) {
      const firstPoint = currentPath[0];
      const distanceMeters = google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(firstPoint.lat, firstPoint.lng),
        event.latLng,
      );
      if (distanceMeters <= CLOSE_POLYGON_DISTANCE_METERS) {
        finishDrawing(currentPath);
        return;
      }
    }
    const newPath = [
      ...currentPath,
      { lat: event.latLng.lat(), lng: event.latLng.lng() },
    ];
    polygonPathRef.current = newPath;
    setPolygonPath(newPath);
  };

  const onPolygonDelete = () => {
    if (newPolygon) {
      // clears the polygon
      newPolygon.setMap(null);
      setNewPolygon(null);
    }

    const { setSelectedVehicleIds, setSearchText } = props;
    // show all vehicles
    setFilteredVehicles(null);
    setSearchText([]);
    setSelectedVehicleIds([]);
    props.updatePolygonVehicles(null);
    resetDrawingState();
    setHasActiveDrawingMode(false);
  };

  const onPolygonButtonClick = () => {
    if (hasActiveDrawingMode) {
      onPolygonDelete();
    } else {
      polygonPathRef.current = [];
      setPolygonPath([]);
      setIsDrawing(true);
      setHasActiveDrawingMode(true);
    }
  };

  const onPressSearch = (e) => {
    if (e.key === 'Enter') {
      goToLocation(e);
    }
  };

  const goToLocation = (e) => {
    const { value } = e.target;

    if (!value) return;

    Geocode.fromAddress(value)
      .then(({ results }) => {
        const { lat, lng } = results[0].geometry.location;

        setFoundCoords({ lat, lng });
        setMapCenter({ lat, lng });
        setMapZoom(LinkVehicleMap.SEARCH_ZOOM_LEVEL);
      })
      .catch((error) => {
        console.warn(error);
      });
  };

  const onClearSearch = () => {
    searchRef.current.children[0].value = null;
    setFoundCoords(null);
  };

  useEffect(() => {
    setHasActiveDrawingMode(JSON.parse(localStorage.getItem('hasActiveDrawingMode')));
  }, []);

  useEffect(() => {
    localStorage.setItem('hasActiveDrawingMode', hasActiveDrawingMode);
  }, [hasActiveDrawingMode]);

  useEffect(() => {
    setMapCenter(getSelectedMarketCoords);
  }, []);

  return (
    <Grid style={{ position: 'relative', width: '100%', height: '100%' }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        zoom={mapZoom}
        center={mapCenter}
        onLoad={(map) => { mapRef.current = map; }}
        onClick={onMapClick}
        options={{
          styles: mapStyles,
          fullscreenControl: true,
          fullscreenControlOptions: {
            position: google.maps.ControlPosition.BOTTOM_RIGHT,
          },
          draggableCursor: isDrawing ? 'crosshair' : undefined,
        }}
      >
        {!!foundCoords && (
          <Marker position={foundCoords} />
        )}
        {isDrawing && polygonPath.length > 0 && (
          <Polyline
            path={polygonPath}
            options={{ strokeColor: '#4285F4', strokeWeight: 2, clickable: false }}
          />
        )}
        {isDrawing && polygonPath.length >= 3 && (
          <Circle
            center={polygonPath[0]}
            radius={CLOSE_POLYGON_DISTANCE_METERS}
            options={{
              strokeColor: '#4285F4',
              fillColor: '#4285F4',
              fillOpacity: 0.35,
              clickable: false,
            }}
          />
        )}
        <CustomMapControl position={google.maps.ControlPosition.TOP_RIGHT}>
          <Button
            style={{
              backgroundColor: 'white',
              borderWidth: 1,
            }}
            sx={{ pl: 1.5, pr: 1, mt: 5, mr: 1.5 }}
            onClick={onPolygonButtonClick}
            variant="outlined"
            color="primary"
          >
            {hasActiveDrawingMode ? 'Remove Boundary' : 'Draw'}
            {hasActiveDrawingMode
              ? (<CloseIcon sx={{ ml: 1, color: 'grey.600' }} />)
              : (<AddIcon sx={{ ml: 1, color: 'grey.600' }} />)}
          </Button>
          {isDrawing && (
            <Grid
              sx={{
                mt: 1, mr: 1.5, p: 1, bgcolor: 'white', borderRadius: 1, fontSize: 12,
              }}
            >
              Click on the map to draw a boundary.
              <br />
              Click the highlighted starting point to finish.
            </Grid>
          )}
        </CustomMapControl>
        <VehicleMarkers
          opacity={filteredVehicles ? 1 : 0.6}
          isClustered={props.isClustered}
          vehicles={filteredVehicles || props.vehicles}
          onClick={(vehicle) => setSelectedMarker(vehicle._id, MarkerTypes.VEHICLE)}
          onClose={deselectMarker}
          fetchOneTask={props.fetchOneTask}
          setOneTask={props.setOneTask}
          selectedVehicleId={
            selectedMarkerType === MarkerTypes.VEHICLE ? selectedMarkerId : null
          }
          openVehicleForm={props.openVehicleForm}
        />
        <LocationMarkers
          zIndex={props.vehicles.length + 2}
          selectedLocationId={
            selectedMarkerType === MarkerTypes.LOCATION
              ? selectedMarkerId
              : null
          }
          locations={props.locations}
          onClose={deselectMarker}
          onClick={(location) => setSelectedMarker(location._id, MarkerTypes.LOCATION)}
          onUpdateLocationTarget={props.onUpdateLocationTarget}
        />
        <WranglerMarkers
          onClose={deselectMarker}
          selectedWranglerId={
            selectedMarkerType === MarkerTypes.WRANGLER
              ? selectedMarkerId
              : null
          }
          wranglers={props.wranglers}
          onClick={(wrangler) => setSelectedMarker(wrangler._id, MarkerTypes.WRANGLER)}
        />
        <ZonePolygons
          showLabels={props.showVehicleZoneLabels}
          zones={props.vehicleZones}
          editable={false}
          vehicles={filteredVehicles || props.vehicles}
        />
      </GoogleMap>
      <TextField
        variant="outlined"
        placeholder="Search for address..."
        onKeyDown={onPressSearch}
        style={{ position: 'absolute', right: 0, top: 0 }}
        sx={{ width: 200, mt: 1.5, mr: 1.5, bgcolor: 'white' }}
        InputProps={{
          ref: searchRef,
          sx: { pr: 1 },
          endAdornment: (!!foundCoords && (
            <InputAdornment
              position="end"
              style={{ cursor: 'pointer' }}
              onClick={onClearSearch}
            >
              <CloseIcon />
            </InputAdornment>
          )),
        }}
      />
      {isOpen && filteredVehicles && (
        <PickupTaskModal
          isOpen={isOpen}
          onClose={handleClose}
          marketId={props.selectedMarket._id}
          vehicles={filteredVehicles}
          setOneTask={props.setOneTask}
        />
      )}
    </Grid>
  );
}

function LinkVehicleMap({
  vehicleZones,
  locations,
  vehicles,
  wranglers,
  selectedMarket,
  onUpdateLocationTarget,
  fetchOneTask,
  setOneTask,
  isClustered,
  updatePolygonVehicles,
  setSelectedVehicleIds,
  setSearchText,
  openVehicleForm,
}) {
  return (
    <div id="map" style={{ height: '500px', width: '100%' }}>
      <BasicMap
        containerElement={<div style={{ height: '100%' }} />}
        mapElement={<div style={{ height: '100%' }} />}
        zoom={LinkVehicleMap.DEFAULT_ZOOM_LEVEL}
        vehicles={vehicles}
        locations={locations}
        vehicleZones={vehicleZones}
        wranglers={wranglers}
        selectedMarket={selectedMarket}
        onUpdateLocationTarget={onUpdateLocationTarget}
        fetchOneTask={fetchOneTask}
        setOneTask={setOneTask}
        isClustered={isClustered}
        updatePolygonVehicles={updatePolygonVehicles}
        setSelectedVehicleIds={setSelectedVehicleIds}
        setSearchText={setSearchText}
        openVehicleForm={openVehicleForm}
      />
    </div>
  );
}

LinkVehicleMap.DEFAULT_ZOOM_LEVEL = 14;
LinkVehicleMap.SEARCH_ZOOM_LEVEL = 17;
LinkVehicleMap.DEFAULT_LAT = 0;
LinkVehicleMap.DEFAULT_LNG = 0;
LinkVehicleMap.DEFAULT_CENTER = {
  lat: LinkVehicleMap.DEFAULT_LAT,
  lng: LinkVehicleMap.DEFAULT_LNG,
};

LinkVehicleMap.defaultProps = {
  vehicles: [],
  locations: [],
  vehicleZones: [],
  wranglers: [],
};
LinkVehicleMap.propTypes = {
  vehicles: PropTypes.arrayOf(PropTypes.object),
  locations: PropTypes.arrayOf(PropTypes.object),
  vehicleZones: PropTypes.arrayOf(PropTypes.object),
  wranglers: PropTypes.arrayOf(PropTypes.object),
};

export default LinkVehicleMap;
